/* Presentation rules for the Suggested rules screen. Pure — no React, no fetch.
 *
 * **Every judgement here is transcribed from the Claude Design reference's own script**, screen 36
 * of `Lumiq App.dc.html`. Two of them are behavioural rather than cosmetic and are the reason this
 * file exists rather than the screen deciding inline:
 *
 * 1. **The AI status is the authority, not the list.** The reference derives its model rows from
 *    the status — `sgAiState === 'contributed' ? all : all.filter(x => x.tier !== 'ai')` — with the
 *    comment that filtering by tier alone "would let a run reporting a rejected answer still show
 *    model rows if one survived serialisation". Our API can produce exactly that: `model_status`
 *    and the stored list are written by different statements, and a regeneration that fails after
 *    storing leaves the previous run's rows readable. So the screen must never show an AI row on a
 *    run whose status is not `contributed`.
 * 2. **`not_configured` absorbs the empty case.** `nothing_to_ask` with no deterministic
 *    suggestions is reported as not-configured, because "the deterministic pass already covered
 *    everything" is a contradiction when it covered nothing.
 *
 * A previous version of this file composed an English claim sentence per suggestion
 * (`claimOf`). That came from an *older* export of the design — `standalone-src.dc.html`, 38
 * screens against 43 — whose Suggestions screen is a list of claims. The authoritative screen is a
 * table whose first cell is the raw `kind` in the mono face, so there is no sentence to compose and
 * the function is gone rather than kept for a caller that no longer exists.
 */

import type {
  CandidateSource,
  ExistingRule,
  GenerationOutcome,
  GenerationRun,
  ModelStatus,
  Suggestion,
} from '../api/rulegen';

/** The four evidence tiers, strongest first. **Measured sits between declared and inferred**: a
 * profile proves what the data was, which is stronger than a naming convention and weaker than a
 * constraint the database enforces. */
export const TIER_ORDER: readonly CandidateSource[] = [
  'schema_declared',
  'profile_observed',
  'schema_inferred',
  'model_proposed',
];

/** How a tier is presented: its i18n key stem and its colour modifier.
 *
 * `satisfies Record<CandidateSource, …>` makes a fourth source a compile error rather than an
 * unlabelled card — the total-map discipline this codebase adopted after a `switch` with a
 * `default:` rendered an unknown verdict in the *permitted* tone.
 */
const TIER = {
  schema_declared: { key: 'suggestions.source.declared', modifier: 'declared' },
  profile_observed: { key: 'suggestions.source.profile', modifier: 'measured' },
  schema_inferred: { key: 'suggestions.source.inferred', modifier: 'inferred' },
  model_proposed: { key: 'suggestions.source.model', modifier: 'ai' },
} satisfies Record<CandidateSource, { readonly key: string; readonly modifier: string }>;

/** The i18n key stem for a tier's title, badge, subtitle and meaning.
 *
 * An unrecognised source is presented as the model tier, which is the same answer every sibling
 * gives it: the tier that asks a reviewer to scrutinise hardest. Reachable because the generated
 * envelope guard checks `Array.isArray` and does not run the item guard, so a fourth source from a
 * server one version ahead arrives here with the type still claiming it cannot.
 */
export function tierKey(source: CandidateSource): string {
  const table: Readonly<Record<string, { readonly key: string } | undefined>> = TIER;
  return (table[source] ?? TIER.model_proposed).key;
}

/** The tier's colour modifier suffix, for `.lq-sg__tier--*` and `.lq-sg__chip--*`. */
export function tierModifier(source: CandidateSource): string {
  const table: Readonly<Record<string, { readonly modifier: string } | undefined>> = TIER;
  return (table[source] ?? TIER.model_proposed).modifier;
}

/** Whether a tier's chip carries a filled dot or a hollow ring.
 *
 * The reference fills the dot for declared and inferred and leaves the AI one transparent — so
 * "a model proposed this" is distinguishable from an enforced fact without relying on hue, which
 * is what makes the distinction survive greyscale.
 */
export function tierDotIsFilled(source: CandidateSource): boolean {
  return source !== 'model_proposed';
}

/** How an AI status is presented. One entry per `ModelStatus` member. */
const AI = {
  contributed: { tone: 'ok', filled: true },
  not_configured: { tone: 'unknown', filled: false },
  no_route: { tone: 'unknown', filled: false },
  unavailable: { tone: 'warn', filled: true },
  rejected: { tone: 'warn', filled: true },
  nothing_to_ask: { tone: 'unknown', filled: false },
  /* A run after a harvest, where AI is deliberately not asked. Neutral, because nothing failed and
     nothing is missing: the model is asked when the table is profiled or generated again. */
  deferred: { tone: 'unknown', filled: false },
} satisfies Record<ModelStatus, { readonly tone: string; readonly filled: boolean }>;

export interface AiPresentation {
  /** `suggestions.ai.<status>`; `.note` appended gives the sentence beneath it. */
  readonly key: string;
  /** `ok` | `warn` | `unknown`, for `.lq-sg__chip--*` and the tile's top rule. */
  readonly tone: string;
  readonly filled: boolean;
}

/** The status a run should be *presented* as, which is not always the status it reports.
 *
 * `nothing_to_ask` with nothing deterministic to show becomes `not_configured`, because "the
 * deterministic pass already covered every column worth suggesting" is a false sentence on a run
 * that produced no suggestion at all. The reference derives this rather than trusting its seed,
 * with the note that the contradiction is derivable and a future dataset would reintroduce it.
 */
export function presentedStatus(
  status: ModelStatus,
  deterministicCount: number,
): ModelStatus {
  return status === 'nothing_to_ask' && deterministicCount === 0
    ? 'not_configured'
    : status;
}

export function aiPresentation(status: ModelStatus): AiPresentation {
  const table: Readonly<
    Record<string, { readonly tone: string; readonly filled: boolean } | undefined>
  > = AI;
  const found = table[status] ?? AI.not_configured;
  return { key: `suggestions.ai.${status}`, tone: found.tone, filled: found.filled };
}

/** The suggestions a run may show. **Filtered by status, never by tier alone.**
 *
 * This is the reference's invariant and the screen's central honesty property: a model row appears
 * only when the run's status is `contributed`. Our two writes are separate — the stored suggestion
 * rows and the outcome's status come from different statements — so a run that stored a model
 * candidate and then reported `rejected` is representable, and filtering on `source` alone would
 * render "AI Suggested" for a run whose own status says nothing from the model was accepted.
 */
export function visibleSuggestions(
  suggestions: readonly Suggestion[],
  status: ModelStatus,
): readonly Suggestion[] {
  if (status === 'contributed') return suggestions;
  /* A *decided* model row stays visible whatever the latest run says. A person already drafted or
     dismissed it, so it is a record of their decision rather than a claim about this run — and
     hiding a drafted row would hide the rule it produced from the one screen that tracks it. */
  return suggestions.filter(
    (item) => item.source !== 'model_proposed' || item.status !== 'pending',
  );
}

/** Suggestions grouped by tier, strongest first, omitting a tier with nothing in it.
 *
 * An empty tier is left out rather than shown empty: an "AI Suggested" heading over no rows reads
 * as a failure, and the reference puts the status chip and its sentence there instead — see
 * `showsAiStandIn`.
 */
export function groupedByTier(suggestions: readonly Suggestion[]): readonly {
  readonly source: CandidateSource;
  readonly items: readonly Suggestion[];
}[] {
  const known = new Set<string>(TIER_ORDER);
  /* A source the union does not contain is grouped with the model tier rather than dropped. The
     first version of this filtered on the three literals and lost anything else, while `tierKey`
     and `tierModifier` gave an unknown source the model answer — three functions disagreeing about
     one unknown in one file. */
  const unknown = suggestions.filter((item) => !known.has(item.source));
  return TIER_ORDER.map((source) => ({
    source,
    items:
      source === 'model_proposed'
        ? [...suggestions.filter((item) => item.source === source), ...unknown]
        : suggestions.filter((item) => item.source === source),
  })).filter((group) => group.items.length > 0);
}

/** Whether the status chip stands in for an absent AI group.
 *
 * True when there is something to show and no model contributed — so the reader is told why there
 * is no AI tier rather than being left to notice its absence. False on an empty run, where the
 * empty state already carries the explanation.
 */
export function showsAiStandIn(status: ModelStatus, visibleCount: number): boolean {
  return status !== 'contributed' && visibleCount > 0;
}

/** One of the four figures above the tiers. */
export interface Tile {
  readonly value: string;
  /** i18n key for the uppercase label. */
  readonly label: string;
  /** i18n key for the sentence beneath it. */
  readonly hint: string;
  /** Interpolation for `hint`, when it takes any. */
  readonly hintValues?: Readonly<Record<string, number>>;
  /** `''` for ink, or a `.lq-sg__tile-value--*` suffix. */
  readonly modifier: string;
}

/** Past a dozen, a refusal count stops being the validator working. The reference's threshold. */
export const REJECTED_ATTENTION = 12;

/** The reference's four tiles, computed from one run's outcome and the rows it may show.
 *
 * `visibleCount` rather than `outcome.accepted`, because the status filter above can hide model
 * rows the run did accept — and a headline figure that counted rows the table does not show would
 * be a contradiction on the same screen.
 */
export function tilesFor(
  outcome: GenerationOutcome,
  visible: readonly Suggestion[],
): readonly Tile[] {
  const total = visible.length;
  const withoutModel = visible.filter((item) => item.source !== 'model_proposed').length;
  const withModel = total - withoutModel;
  const refused = totalRejected(outcome.rejected);

  return [
    {
      value: String(outcome.columns_considered),
      label: 'suggestions.tile.columns',
      hint: 'suggestions.tile.columns.hint',
      modifier: '',
    },
    {
      value: String(total),
      label: 'suggestions.tile.total',
      hint:
        total === 0
          ? 'suggestions.tile.total.none'
          : withModel > 0
            ? 'suggestions.tile.total.tiers'
            : 'suggestions.tile.total.deterministic',
      modifier: '',
    },
    {
      /* **A dash, never 0%.** Zero percent is a claim about the generator; an empty run is a claim
         about nothing, and 0% would read as "a model did all the work" on a run that produced
         none. The reference's own reasoning, in its own words. */
      value: total === 0 ? '—' : `${String(Math.round((withoutModel / total) * 100))}%`,
      label: 'suggestions.tile.share',
      hint: total === 0 ? 'suggestions.tile.share.none' : 'suggestions.tile.share.hint',
      hintValues: { withoutModel, total },
      modifier: '',
    },
    {
      value: String(refused),
      label: 'suggestions.tile.rejected',
      hint:
        refused === 0
          ? 'suggestions.tile.rejected.none'
          : 'suggestions.tile.rejected.hint',
      /* Neutral, not red. A candidate naming a column that is not on this dataset is the
         validator working, not an incident — so only an unusually high count takes amber. */
      modifier: refused > REJECTED_ATTENTION ? 'warn' : refused === 0 ? 'quiet' : 'quiet',
    },
  ];
}

/** How many candidates were refused, in total. `0` is a real and common answer.
 *
 * **Counts only, and the contract is why: a rejection detail can name a column, and a rejected
 * *model* candidate is the one place a column name may not be one we sent.** So there is
 * deliberately no function here returning a rejected candidate — there is nothing in the payload
 * to return, and the reference's own tile hint says "counted by reason, never shown".
 */
export function totalRejected(rejected: Readonly<Record<string, number>>): number {
  return Object.values(rejected).reduce((sum, count) => sum + count, 0);
}

/** A suggestion's parameters as `key = value` pairs, sorted, or a dash.
 *
 * Sorted so the same rule renders identically between refreshes — an unsorted object reorders on a
 * re-fetch and looks like a change to a suggestion that has not changed. The reference joins with
 * `; ` and falls back to an em dash, both transcribed.
 */
export function parameterText(parameters: Readonly<Record<string, string>>): string {
  const pairs = Object.entries(parameters)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key} = ${value}`);
  return pairs.length > 0 ? pairs.join('; ') : '—';
}

export const PROVENANCE_PREFIX = 'suggested-from:';
/** The tag prefix a created rule carries. **Mirrors `SUGGESTION_TAG_PREFIX` on the server.**
 *
 * Two copies of a string is normally the two-vocabularies defect this codebase records at several
 * sites, and it is accepted here for one reason: the *server* mints the tag and puts it on
 * `RuleDraft.tags`, so a screen never composes one. This constant exists only to *read* a tag back
 * off a rule, which is a parse rather than a second authority — and if the two ever disagreed, the
 * failure is that a prefill does not offer to seed, never that a wrong rule is created.
 */

/** The suggestion a rule was created from, or `null` if it was authored by hand.
 *
 * **This is what makes the prefill survive a reload.** Creating a rule from a suggestion is two
 * calls — the columns and parameters live on the *version* — and router state does not cross a
 * refresh, so without a durable reference the second hop would arrive with the values gone.
 *
 * Returns the *first* match rather than refusing several: a rule with two provenance tags is not a
 * state this platform produces, and answering `null` there would drop the prefill for a rule that
 * plainly has one.
 */
export function suggestionIdFromTags(tags: readonly string[] | undefined): string | null {
  for (const tag of tags ?? []) {
    if (tag.startsWith(PROVENANCE_PREFIX)) {
      const id = tag.slice(PROVENANCE_PREFIX.length).trim();
      if (id !== '') return id;
    }
  }
  return null;
}

/** How many suggestions one dataset has. **`null` for a dataset the list does not name.**
 *
 * `null` rather than `0`, and the distinction is the contract's: a dataset with no suggestions is
 * absent from the answer, so a missing key means *nobody has generated for this dataset* while a
 * zero would claim a run happened and found nothing worth suggesting.
 */
export function suggestionCountFor(
  counts: readonly { readonly dataset_id: string; readonly suggestions: number }[],
  datasetId: string,
): number | null {
  const found = counts.find((entry) => entry.dataset_id === datasetId);
  return found === undefined ? null : found.suggestions;
}

/* ------------------------------------------------------------------ the review lifecycle */

/** How strongly the evidence supports a recommendation, in three words a reader can act on.
 *
 * The thresholds follow the server's own constants: a declared constraint is 0.95, a profile
 * measurement 0.65–0.85, a naming guess 0.55, a model proposal 0.5, and a guess the profile
 * contradicted 0.25. So "high" is a declaration or a well-sampled measurement and "low" is a guess
 * the data argues against.
 */
export type ConfidenceBand = 'high' | 'medium' | 'low';

export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

/** The confidence as a whole percentage. Never rounded up to 100: only a measurement is certain. */
export function confidencePercent(confidence: number): string {
  return `${String(Math.min(99, Math.round(confidence * 100)))}%`;
}

/** Where one recommendation stands, combining what a person did with what `core.rules` reports.
 *
 * **The rule's state is read live** — the server never stores whether a drafted rule is active, so
 * this is the only place the two are joined, and it is a rendering rather than a fact anybody can
 * act on without the API agreeing.
 */
export type Stage =
  | 'open'
  | 'awaiting_approval'
  | 'activation_not_requested'
  | 'ready_to_bind'
  | 'monitoring'
  | 'rule_missing'
  | 'dismissed';

export function stageOf(item: Suggestion): Stage {
  if (item.status === 'pending') return 'open';
  if (item.status === 'dismissed') return 'dismissed';
  if (item.status === 'bound') return 'monitoring';
  const rule = item.rule;
  if (rule === null || rule.exists === false || rule.state === null)
    return 'rule_missing';
  if (rule.state === 'active') return 'ready_to_bind';
  if (rule.state === 'deprecated') return 'rule_missing';
  return rule.activation_pending === true
    ? 'awaiting_approval'
    : 'activation_not_requested';
}

/** Tone per stage, total over `Stage` so a new stage is a compile error rather than a grey badge. */
export const STAGE_TONE = {
  open: 'unknown',
  awaiting_approval: 'warn',
  activation_not_requested: 'warn',
  ready_to_bind: 'info',
  monitoring: 'ok',
  rule_missing: 'warn',
  dismissed: 'unknown',
} satisfies Record<Stage, string>;

export type StatusFilter = 'open' | 'applied' | 'dismissed' | 'all';

export interface SuggestionFilter {
  readonly status: StatusFilter;
  /** `''` means any. */
  readonly category: string;
  /** `''` means any; otherwise `system`, `ai` or `hybrid`. See `originMatches`. */
  readonly origin: string;
  /** Case-insensitive substring of any column. */
  readonly column: string;
}

export const NO_FILTER: SuggestionFilter = {
  status: 'open',
  category: '',
  origin: '',
  column: '',
};

/**
 * Whether a recommendation belongs under a Source choice. **Inclusive, not exact.**
 *
 * `hybrid` means Lumiq proposed it *and* the AI independently proposed the same rule, so it is a
 * Lumiq recommendation and an AI recommendation at once. Matching exactly made "Lumiq" hide every
 * recommendation the AI had agreed with — the strongest ones — and made the agreement choice look
 * broken whenever no model ran, since agreement needs a model. So `system` admits `hybrid`, `ai`
 * admits `hybrid`, and `hybrid` is only the agreed ones.
 */
export function originMatches(origin: string, choice: string): boolean {
  if (choice === '') return true;
  if (choice === 'hybrid') return origin === 'hybrid';
  return origin === choice || origin === 'hybrid';
}

export function matchesFilter(item: Suggestion, filter: SuggestionFilter): boolean {
  const statusOk =
    filter.status === 'all' ||
    (filter.status === 'open' && item.status === 'pending') ||
    (filter.status === 'applied' &&
      (item.status === 'drafted' || item.status === 'bound')) ||
    (filter.status === 'dismissed' && item.status === 'dismissed');
  const needle = filter.column.trim().toLowerCase();
  return (
    statusOk &&
    (filter.category === '' || item.category === filter.category) &&
    originMatches(item.origin, filter.origin) &&
    (needle === '' ||
      item.columns.some((column) => column.toLowerCase().includes(needle)))
  );
}

/** How many recommendations each Source choice would show, with every other filter applied.
 *
 * Shown beside each choice so an empty one reads as "none of these here" before it is picked — the
 * agreement choice is empty on any run where no model was asked, which is the normal case. */
export function originCounts(
  items: readonly Suggestion[],
  filter: SuggestionFilter,
): Readonly<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const choice of ['', 'system', 'ai', 'hybrid']) {
    counts[choice] = items.filter((item) =>
      matchesFilter(item, { ...filter, origin: choice }),
    ).length;
  }
  return counts;
}

/** Whether any filter other than the default is applied, which is when "Clear filters" is offered. */
export function isFiltered(filter: SuggestionFilter): boolean {
  return (
    filter.status !== NO_FILTER.status ||
    filter.category !== NO_FILTER.category ||
    filter.origin !== NO_FILTER.origin ||
    filter.column.trim() !== ''
  );
}

/** How many recommendations each status filter would show — the counts on the filter buttons. */
export function statusCounts(
  items: readonly Suggestion[],
): Readonly<Record<StatusFilter, number>> {
  return {
    open: items.filter((item) => item.status === 'pending').length,
    applied: items.filter((item) => item.status === 'drafted' || item.status === 'bound')
      .length,
    dismissed: items.filter((item) => item.status === 'dismissed').length,
    all: items.length,
  };
}

/** The evidence figures, in the server's own order of importance, as i18n key and value. */
const EVIDENCE_ORDER = ['rows_profiled', 'nulls_observed', 'distinct_values'] as const;

/** Evidence in words a reader does not have to decode. **A zero null count gets its own sentence**
 * — "No empty values" — because "0 nulls" makes the strongest finding read like a missing figure. */
export function evidenceItems(
  evidence: Readonly<Record<string, string>>,
): readonly { readonly key: string; readonly value: string }[] {
  return EVIDENCE_ORDER.filter((key) => evidence[key] !== undefined).map((key) => {
    const value = evidence[key] ?? '';
    if (key === 'nulls_observed' && value === '0') {
      return { key: 'suggestions.evidence.no_nulls', value };
    }
    return { key: `suggestions.evidence.${key}`, value };
  });
}

/** Columns as a reader says them: "A", "A and B", or "A and 3 more". */
export function columnsLabel(columns: readonly string[]): string {
  const [first, second] = columns;
  if (first === undefined) return '';
  if (second === undefined) return first;
  if (columns.length === 2) return `${first} and ${second}`;
  return `${first} and ${String(columns.length - 1)} more`;
}

/** The kinds with a plain-language headline. Anything else falls back to a generic sentence. */
const HEADLINED = new Set([
  'not_null',
  'unique',
  'freshness',
  'range',
  'pattern',
  'accepted_values',
  'row_count_anomaly',
  'referential_integrity',
]);

/** The table a referential check points at — `sales.customers` out of `sales.customers.id`. A
 * host-written parameter naming a table in the same source, never a value from the data. */
function parentOf(references: string | undefined): string {
  if (references === undefined) return '…';
  const parts = references.split('.');
  return parts.length > 1 ? parts.slice(0, -1).join('.') : references;
}

/** One sentence saying what the recommended rule checks, e.g. "CUSTOMER_SK must always have a
 * value". The technical kind stays beside it as a small tag for the people who want it.
 *
 * Parameters that are content — a regex, a list of values — are not quoted into the sentence: the
 * sentence says the kind of check, and the editor shows the exact value. */
export function headlineOf(item: {
  readonly kind: string;
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
}): { readonly key: string; readonly values: Readonly<Record<string, string>> } {
  const values: Record<string, string> = { columns: columnsLabel(item.columns) };
  if (item.kind === 'freshness') {
    values['hours'] = item.parameters['max_age_hours'] ?? '24';
  }
  if (item.kind === 'range') {
    values['min'] = item.parameters['min'] ?? '…';
    values['max'] = item.parameters['max'] ?? '…';
  }
  if (item.kind === 'referential_integrity') {
    values['parent'] = parentOf(item.parameters['references']);
  }
  return {
    key: HEADLINED.has(item.kind)
      ? `suggestions.headline.${item.kind}`
      : 'suggestions.headline.other',
    values: { ...values, kind: item.kind },
  };
}

/** How an existing rule relates to this table, as an i18n key — the sentence under its name. */
export function coverageKey(rule: ExistingRule): string {
  return `suggestions.reuse.coverage.${rule.coverage}`;
}

/** The severities a person may pick for an existing rule: every one it does not already use here.
 * A second binding at a used severity would collide with the first, so it is not offered. */
export function freeSeverities(
  rule: ExistingRule,
  all: readonly string[],
): readonly string[] {
  return all.filter((severity) => !rule.bound_severities.includes(severity));
}

/** The latest stored run as an outcome, so the figures can render after an automatic run.
 *
 * Only the fields the tiles and the AI status read are meaningful. `model_candidates` is not
 * stored with a run and is reported as 0, which nothing on the screen displays.
 */
export function outcomeFromRun(datasetId: string, run: GenerationRun): GenerationOutcome {
  return {
    dataset_id: datasetId,
    columns_considered: run.columns_considered,
    accepted: run.accepted,
    by_source: run.by_source,
    rejected: run.rejected,
    model_consulted: run.model_status === 'contributed',
    model_status: run.model_status,
    model_candidates: 0,
    model_unavailable_reason: run.model_detail,
    profile_status: run.profile_status,
    observations: run.observations,
    already_decided: 0,
    trigger: run.trigger,
  };
}

/** Parameters a person may edit on apply, for one kind — from the capabilities response. */
export function editableParameters(
  editable: Readonly<Record<string, readonly string[]>> | undefined,
  kind: string,
): readonly string[] {
  return editable?.[kind] ?? [];
}
